/**
 * CardPaymentPane — Paket B (premium-checkout-redesign)
 *
 * Karten-Tab von /checkout. Stripe-Elements-Logik 1:1 aus
 * subscription/CheckoutModal.tsx übernommen (dort NICHT geändert) — eigene
 * lazy Stripe-Instanz, da CheckoutModal seine nicht exportiert.
 *
 * Bei bestehendem aktivem Abo (status !== 'expired') gibt es KEIN
 * Kartenformular: Stripe verrechnet den Restwert serverseitig beim
 * Planwechsel (PATCH /api/v2/subscription), daher genügt ein Bestätigen-Button.
 */
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { Stripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import type { Plan } from '../subscription/useSubscriptionPlans'
import type { DurationMonths } from '../subscription/subscriptionUtils'
import type { SubscribeResult, Subscription } from '../subscription/useSubscription'
import { AlreadySubscribedError } from '../subscription/useSubscription'
import { apiErrorStatus } from '../../lib/errors/apiErrorMessage'
import { formatMoney } from '../premium/planMath'
import { ConversionConfirm } from './ConversionConfirm'

let stripePromise: Promise<Stripe | null> | null = null

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    // Siehe CheckoutModal.tsx: '/pure' lädt js.stripe.com erst bei Bedarf,
    // nicht schon beim Boot jeder Seite.
    stripePromise = import('@stripe/stripe-js/pure').then(({ loadStripe }) =>
      loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? ''),
    )
  }
  return stripePromise
}

function getStripeTheme(): 'night' | 'stripe' {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'night' : 'stripe'
}

function isStripeTestMode(): boolean {
  return (import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '').startsWith('pk_test')
}

type CardPaymentPaneProps = {
  plan: Plan
  duration: DurationMonths
  amountDue: number
  subscription: Subscription | null | undefined
  subscribe: (paymentMethodId: string, planId: string, durationMonths: number) => Promise<SubscribeResult>
  changePlan: (planId: string, durationMonths: number) => Promise<void>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }>
  onSuccess: () => void
  // Issue BE#24: welcher Pfad greift bei Bestätigung (siehe usePreviewUpgrade).
  // 'checkout' (Default) = kein aktives Abo -> Kartenformular.
  mode?: 'stripe_swap' | 'conversion' | 'checkout'
  conversionExpiresAt?: string | null
  daysRemaining?: number
  // Solange die Vorschau für ein bestehendes Abo lädt, ist mode noch nicht
  // verlässlich (Default 'checkout') — ein Skeleton verhindert das kurze aber
  // falsche Aufblitzen des Kartenformulars.
  previewLoading?: boolean
}

/** Planwechsel bei bestehendem aktivem Abo — keine neue Karteneingabe nötig. */
function PlanChangeConfirm({
  plan,
  duration,
  amountDue,
  subscription,
  changePlan,
  onSuccess,
}: Pick<CardPaymentPaneProps, 'plan' | 'duration' | 'amountDue' | 'subscription' | 'changePlan' | 'onSuccess'>) {
  const { t } = useTranslation('checkout')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const method = subscription?.payment_method

  const handleConfirm = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      await changePlan(plan.id, duration)
      onSuccess()
    } catch {
      setError(t('card.paymentError'))
    } finally {
      setIsLoading(false)
    }
  }, [changePlan, plan.id, duration, onSuccess, t])

  return (
    <div data-testid="card-plan-change">
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          {method
            ? t('card.existingMethodLabel', { brand: method.brand, last4: method.last4 })
            : t('card.noMethod')}
        </p>
      </div>
      {error && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary btn-lg"
        style={{ width: '100%' }}
        disabled={isLoading}
        onClick={() => { void handleConfirm() }}
        data-testid="confirm-plan-change-btn"
      >
        {isLoading
          ? t('card.confirmPlanChangeLoading')
          : t('card.confirmPlanChange', { amount: formatMoney(amountDue, plan.currency) })}
      </button>
    </div>
  )
}

/** Neues Karten-Abo — identischer Ablauf zu CheckoutModal#CheckoutForm. */
function NewCardForm({
  plan,
  duration,
  amountDue,
  subscribe,
  refetchSubscription,
  onSuccess,
}: Pick<CardPaymentPaneProps, 'plan' | 'duration' | 'amountDue' | 'subscribe' | 'refetchSubscription' | 'onSuccess'>) {
  const { t } = useTranslation('checkout')
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)
  const [alreadySubscribed, setAlreadySubscribed] = useState(false)

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return
    setIsLoading(true)
    setCardError(null)
    setAlreadySubscribed(false)

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setIsLoading(false)
      return
    }

    const pmResult = await stripe.createPaymentMethod({ type: 'card', card: cardElement })
    if (pmResult.error) {
      setCardError(pmResult.error.message ?? t('card.cardError'))
      setIsLoading(false)
      return
    }

    try {
      const res = await subscribe(pmResult.paymentMethod.id, plan.id, duration)
      if (res.requiresAction) {
        const confirm = await stripe.confirmCardPayment(res.clientSecret)
        if (confirm.error) {
          setCardError(confirm.error.message ?? t('card.paymentError'))
          setIsLoading(false)
          return
        }
        await refetchSubscription()
      }
      onSuccess()
    } catch (err: unknown) {
      if (err instanceof AlreadySubscribedError) {
        setAlreadySubscribed(true)
      } else if (apiErrorStatus(err) === 429) {
        setCardError(t('card.rateLimited'))
      } else {
        setCardError(t('card.paymentError'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [stripe, elements, plan.id, duration, subscribe, refetchSubscription, onSuccess, t])

  return (
    <div data-testid="card-payment-form">
      <div className="field">
        <label className="label" htmlFor="cc-name">{t('card.nameLabel')}</label>
        <input className="input" id="cc-name" placeholder={t('card.namePlaceholder')} autoComplete="cc-name" />
      </div>
      <div className="field">
        <label className="label" htmlFor="card-element-wrapper">{t('card.cardLabel')}</label>
        <div
          id="card-element-wrapper"
          data-testid="card-element-wrapper"
          style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', background: 'var(--surface)' }}
        >
          <CardElement
            options={{ style: { base: { fontSize: '14px', color: 'var(--text)', '::placeholder': { color: 'var(--text-subtle)' } } } }}
          />
        </div>
      </div>

      {isStripeTestMode() && (
        <p data-testid="checkout-testmode-hint" style={{ color: 'var(--text-subtle)', fontSize: 12, marginBottom: 12 }}>
          {t('card.stripeHint')}
        </p>
      )}

      {alreadySubscribed && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }} data-testid="already-subscribed-hint">
          {t('card.alreadySubscribed')}{' '}
          <Link to="/account/subscription" style={{ color: 'var(--primary)' }}>{t('card.alreadySubscribedLink')}</Link>
        </p>
      )}
      {cardError && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {cardError}
        </p>
      )}

      <button
        type="button"
        className="btn btn-primary btn-lg"
        style={{ width: '100%' }}
        disabled={isLoading || !stripe}
        onClick={() => { void handleSubmit() }}
        data-testid="card-pay-btn"
      >
        {isLoading ? t('card.payButtonLoading') : t('card.payButton', { amount: formatMoney(amountDue, plan.currency) })}
      </button>
      <p className="muted" style={{ fontSize: 11, textAlign: 'center', margin: '10px 0 0' }}>
        {t('card.stripeHint')}
      </p>
    </div>
  )
}

export function CardPaymentPane({
  plan,
  duration,
  amountDue,
  subscription,
  subscribe,
  changePlan,
  refetchSubscription,
  onSuccess,
  mode = 'checkout',
  conversionExpiresAt = null,
  daysRemaining,
  previewLoading = false,
}: CardPaymentPaneProps) {
  const [stripeInstance] = useState<Promise<Stripe | null>>(getStripePromise)

  if (previewLoading) {
    return (
      <div data-testid="card-preview-skeleton" className="pulse">
        <div className="skeleton-text" style={{ height: 80, borderRadius: 8 }} />
        <div className="skeleton-text" style={{ height: 44, marginTop: 16, borderRadius: 8 }} />
      </div>
    )
  }

  if (mode === 'conversion') {
    return (
      <ConversionConfirm
        planId={plan.id}
        planName={plan.name}
        duration={duration}
        daysRemaining={daysRemaining}
        conversionExpiresAt={conversionExpiresAt}
        changePlan={changePlan}
        onSuccess={onSuccess}
      />
    )
  }

  if (mode === 'stripe_swap') {
    return (
      <PlanChangeConfirm
        plan={plan}
        duration={duration}
        amountDue={amountDue}
        subscription={subscription}
        changePlan={changePlan}
        onSuccess={onSuccess}
      />
    )
  }

  const appearance = {
    theme: getStripeTheme(),
    variables: { fontFamily: 'var(--font-body, sans-serif)', colorPrimary: 'var(--c-primary, #6366f1)' },
  }

  return (
    <Elements stripe={stripeInstance} options={{ appearance: appearance as import('@stripe/stripe-js').Appearance }}>
      <NewCardForm
        plan={plan}
        duration={duration}
        amountDue={amountDue}
        subscribe={subscribe}
        refetchSubscription={refetchSubscription}
        onSuccess={onSuccess}
      />
    </Elements>
  )
}
